import { Box, Text, Textarea, TextInput } from "@dehub/theme";
import type { UseFormReturnType } from "@dehub/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { IAuth } from "@/models/Auth";
import type { IGeneral } from "@/models/General";
import type { IReference } from "@/models/Reference";
import { renderBusinessName } from "@/utils/render-business-name";
import { NAME_MAX, PROFILE_INFO_MAX, type BusinessFormValues } from "./business-form-model";
import classes from "../business.module.css";

type Props = Readonly<{
  form: UseFormReturnType<BusinessFormValues>;
  readOnly: boolean;
}>;

function ContractItem({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <Box className={classes.contractItem}>
      <Text component="span" className={classes.contractLabel}>
        {label}
      </Text>
      <Text component="span" className={classes.contractValue}>
        {value || "-"}
      </Text>
    </Box>
  );
}

export function InfoStep({ form, readOnly }: Props) {
  const { t } = useTranslation("business");
  const user = useSelector((state: { auth: IAuth }) => state.auth.user);
  const legalEntityTypes = useSelector((state: { general: IGeneral }) => state.general.legalEntityTypes);
  const partner = user?.partner;

  const legalTypeName = useMemo(
    () =>
      (legalEntityTypes as IReference[] | undefined)?.find((item) => item.code === partner?.legalEntityType)?.name ?? "",
    [legalEntityTypes, partner?.legalEntityType],
  );

  const secondary = [partner?.phone2, partner?.email2].filter(Boolean).join(" · ");
  const infoLength = form.values.profileInfo.length;

  return (
    <Box className={classes.stack}>
      <Box className={classes.fieldGrid}>
        <Text component="span" className={`${classes.caps} ${classes.fieldWide}`}>
          {t("form.networkInfo")}
        </Text>
        <TextInput
          required
          name="profileName"
          maxLength={NAME_MAX}
          autoComplete="organization"
          label={t("form.bizName")}
          readOnly={readOnly}
          {...form.getInputProps("profileName")}
        />
        <TextInput
          name="profileNameEng"
          maxLength={NAME_MAX}
          spellCheck={false}
          label={t("form.bizNameLatin")}
          placeholder="NEXT Distribution LLC"
          readOnly={readOnly}
          {...form.getInputProps("profileNameEng")}
        />
        <Box className={classes.fieldWide}>
          <Textarea
            name="profileInfo"
            aria-required="true"
            autosize
            minRows={3}
            maxLength={PROFILE_INFO_MAX}
            readOnly={readOnly}
            label={
              <Box component="span" className={classes.labelRow}>
                <Text component="span">
                  {t("form.bizInfo")}
                  <Text component="span" aria-hidden="true">
                    {" *"}
                  </Text>
                </Text>
                <Text component="span" className={classes.counter}>
                  {infoLength} / {PROFILE_INFO_MAX}
                </Text>
              </Box>
            }
            {...form.getInputProps("profileInfo")}
          />
        </Box>
      </Box>

      <Text component="span" className={classes.caps}>
        {t("form.contractInfo")}
      </Text>
      <Box className={classes.contractGrid}>
        <ContractItem label={t("form.legalName")} value={renderBusinessName(partner)} />
        <ContractItem label={t("form.legalType")} value={legalTypeName} />
        <ContractItem label={t("form.regNumber")} value={partner?.regNumber ?? ""} />
        <ContractItem label={t("form.phone1")} value={partner?.phone ?? ""} />
        <ContractItem label={t("form.email1")} value={partner?.email ?? ""} />
        <ContractItem label={t("form.secondaryContact")} value={secondary} />
      </Box>
      <Text component="span" className={classes.hint}>
        {t("form.infoCheckDesc")}
      </Text>
    </Box>
  );
}
